import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import UnitSwitch from '../components/UnitSwitch';
import { usePersistentValue } from '../hooks/usePersistentState';
import CockpitCfo from './cockpit-cfo/CockpitCfo';
import CockpitFinancement from './cockpit-cfo/CockpitFinancement';
import { updateCockpitTabs } from '../api/tenants';
import { COCKPIT_TABS, COCKPIT_TAB_IDS, visibleCockpitTabs } from '../nav/cockpitTabs';
import './CfoHub.css';

/**
 * COCKPIT — les vues d'analyse de direction (Rentabilité, Liquidité & solidité,
 * Financement). Le Journal et les Décisions, qui vivaient ici, ont leur page
 * depuis le 2026-10-01 (`cockpit-cfo/JournalPage`) : des notes, pas une vue
 * de données.
 */

export default function CfoHub({ activeTenant, year, onTenantPatched, setPage, scopes, activeTab, onTabChange }) {
  const { t } = useTranslation();
  const tenantId = activeTenant?.id || null;
  // Vue active (ex-onglets) : pilotée par le panneau contextuel de la Sidebar
  // via App (`cockpitTab`), comme les hubs Paramètres / Administration.
  const tab = activeTab || 'rent';
  const setTab = onTabChange || (() => {});
  const [unit, setUnit] = usePersistentValue(`cfo_unit_${activeTenant?.id}`, 'kchf');
  // Cockpit modulable : onglets visibles selon la config du tenant (null = tous),
  // éditable en self-service via « Modifier le cockpit ».
  const [editCockpit, setEditCockpit] = useState(false);
  const visibleTabs = useMemo(
    () => visibleCockpitTabs(activeTenant?.cockpit_tabs, scopes, activeTenant?.enabled_modules || []),
    [activeTenant?.cockpit_tabs, scopes, activeTenant?.enabled_modules],
  );
  const visibleIds = visibleTabs.map(vt => vt.id).join(',');
  async function toggleCockpitTab(tabId) {
    if (!tenantId) return;
    const current = Array.isArray(activeTenant?.cockpit_tabs) ? activeTenant.cockpit_tabs : COCKPIT_TAB_IDS;
    const draft = current.includes(tabId) ? current.filter(x => x !== tabId) : [...current, tabId];
    if (!draft.length) return; // au moins un onglet (garde-fou aussi côté serveur)
    const next = COCKPIT_TAB_IDS.filter(id => draft.includes(id));
    const res = await updateCockpitTabs(tenantId, next);
    if (res?.data && onTenantPatched) onTenantPatched(res.data);
  }
  // Si la vue active est masquée (config ou changement de client), retomber sur
  // la 1re vue visible.
  useEffect(() => {
    const ids = visibleIds.split(',').filter(Boolean);
    if (ids.length && !ids.includes(tab)) setTab(ids[0]);
  }, [visibleIds, tab]); // eslint-disable-line

  const DATA_TABS = new Set(['rent', 'liq', 'fin']);

  // Bouton « Modifier le cockpit » dans l'en-tête de page, DEVANT le toggle
  // CHF/kCHF ; ouvre le mode config (vues visibles + masquage des KPI).
  const cockpitEditBtn = (
    <button type="button" className={`ckpit__editbtn${editCockpit ? ' is-on' : ''}`}
      onClick={() => setEditCockpit(v => !v)} disabled={!tenantId}>
      {editCockpit ? t('cockpit:editButton.done') : t('cockpit:editButton.edit')}
    </button>
  );

  /* Rendu d'une vue de données. LE COCKPIT N'EXPORTE RIEN (2026-10-09) : ses
     trois vues sont des tuiles, des jauges et des graphiques, aucun <table> à
     donner, et l'export répondait « Aucune donnée à exporter ». L'icône
     télécharger s'atténue, comme sur toute page qui ne déclare rien. */
  const renderDataTab = (id) => {
    switch (id) {
      case 'rent':
      case 'liq':
        return <CockpitCfo activeTenant={activeTenant} year={year} unit={unit} view={id}
          editMode={editCockpit} onGoTab={setPage} />;
      case 'fin':   return <CockpitFinancement tenantId={tenantId} year={year} unit={unit} />;
      default: return null;
    }
  };

  return (
    <PageShell
      framed
      title={t('cockpit:title')}
      actions={<>{cockpitEditBtn}{DATA_TABS.has(tab) ? <UnitSwitch value={unit} onChange={setUnit} /> : null}</>}
    >
      {/* Les ex-onglets du Cockpit vivent dans le panneau du menu (Sidebar) —
          plus de barre d'onglets interne. */}

      {/* Config self-service des vues visibles (mode « Modifier le cockpit »). */}
      {editCockpit && (
        <div className="ckpit-config">
          <span className="ckpit-config__lbl">{t('cockpit:config.visibleTabs')}</span>
          <div className="ckpit-config__list">
            {COCKPIT_TABS.map(vt => {
              const on = !activeTenant?.cockpit_tabs || activeTenant.cockpit_tabs.includes(vt.id);
              return (
                <label key={vt.id} className={`ckpit-config__item${on ? ' is-on' : ''}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleCockpitTab(vt.id)} />
                  <span>{t(vt.labelKey)}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div className="page-shell__panel">
        {DATA_TABS.has(tab) && renderDataTab(tab)}

      </div>

    </PageShell>
  );
}
